<script setup lang="ts">
import type { App } from "~/models/App";
import type { Connection } from "~/models/Connection";
import {
    faSpinnerThird,
    faCheckDouble,
    faLayerGroup,
    faPause
} from "@fortawesome/pro-regular-svg-icons";

import {
    faExclamationTriangle,
    faCircle,
    faCog,
    faSave,
    faDatabase,
    faSyncAlt,
    faInfoCircle,
    faQuestionCircle,
    faExclamationCircle,
    faFileAlt,
    faFileUpload,
    faPhoneSquare,
    faPhone,
    faPhoneAlt,
} from "@fortawesome/pro-solid-svg-icons";
import {
    faUserCheck,
    faUserFriends,
    faUsers,
} from "@fortawesome/pro-duotone-svg-icons";
import {validate} from "uuid";

const { app = undefined, status = undefined, size = undefined, strategy = 'fixed' } = defineProps<{
    app?: App | Connection
    status?: string
    size?: string
    strategy?: 'absolute' | 'fixed'
}>();

const statuses = {
    active: [faCircle, "text-primary-500"],
    inactive: [faCircle, "text-gray-400"],
    draft: [faCircle, "text-gray-400"],
    inactive_connections: [faCircle, "text-orange-400"],
    successful: [faCircle, "text-primary-500"],
    failed: [faExclamationTriangle, "text-red-600"],
    synced_user: [faUserCheck, "text-primary-500"],
    provisioning_started: [faSpinnerThird, "text-primary-500", { spin: true }],
    caching_tenant_config: [faDatabase, "text-primary-500"],
    cached_settings: [faCog, "text-purple-500"],
    error: [faExclamationTriangle, "text-red-500"],
    warning: [faExclamationTriangle, "text-red-500"],
    cached_tenant_config: [faSave, "text-primary-500"],
    syncing_objects: [faSyncAlt, "text-yellow-400"],
    paused: [faPause, "text-yellow-400"],
    synced_object: [faCheckDouble, "text-primary-500"],
    objects_synced: [faLayerGroup, "text-cyan-500"],
    syncing_users: [faUserFriends, "text-blue-400"],
    users_synced: [faUsers, "text-blue-600"],
    provisioning_finished: [faCircle, "text-primary-500"],
    provisioning_error: [faExclamationCircle, "text-red-500"],
    caching_information: [faInfoCircle, "text-gray-500"],
    phone_numbers_synced: [faPhoneAlt, "text-primary-500"],
    syncing_templates: [faFileAlt, "text-teal-400"],
    templates_synced: [faFileUpload, "text-teal-500"],
    syncing_phone_numbers: [faPhoneSquare, "text-green-400"],
    synced_phone_number: [faPhone, "text-primary-500"],
    unknown: [faQuestionCircle, "text-gray-500"]
};

const internalStatus = computed(() => {
    if(status) return status;

    if (app) {
        const isCustomApp = validate(app.uuid);

        if ("provisioning_status" in app && app.provisioning_status) return app.provisioning_status;

        if(app.connections?.length) {
            let last_provision_status = "active";

            for(const connection of app.connections) {
                if ("provisioning_status" in connection && connection.provisioning_status) (last_provision_status = connection.provisioning_status);
                if ("is_failed" in connection && connection.is_failed) return last_provision_status;
                if ("active" in connection && !connection.active) return "inactive";
            }

            return last_provision_status;
        } else if(app.triggers?.length || isCustomApp) {
            for(const trigger of app.triggers ?? []) {
                if(trigger.status === "active") return "active";
            }

            return app.status;
        }

        if ("active" in app && app.active) return "active";

        return app.connections?.length ? "inactive_connections" : "inactive";
    }

    return status ?? "unknown";
});

const icon = computed(() => {
    if(statuses[internalStatus.value]) {
        return statuses[internalStatus.value];
    }
    return statuses.unknown;
});

const { t } = useI18n();
const tooltip = computed(() => t(`enums.app_status.${internalStatus.value}`))
</script>

<template>
    <HdPopoverTip v-if="tooltip" :text="tooltip" :popper="{ placement: 'top', strategy }" class="z-10">
        <template #trigger>
            <FontAwesomeIcon fixed-width :size="size" :icon="icon[0]" :class="icon[1]" v-bind="icon[2]" />
        </template>
        <div class="p-2 text-left w-72 bg-white z-10">{{ tooltip }}</div>
    </HdPopoverTip>
    <FontAwesomeIcon v-else fixed-width :size="size" :icon="icon[0]" :class="icon[1]" v-bind="icon[2]" />
</template>